import type { ReactNode } from "react";
import { SelectOption } from "@cinesys/ui/components";
import { IconikMetadataField, IconikMetadataView } from "../mapping/service";

/** One row's worth of context for the flattened field list — every field across both configured
 *  views (mirrors `../mapping/addField.ts`'s now-retired `FlatFieldEntry`). */
export interface FlatFieldEntry {
  view: IconikMetadataView;
  field: IconikMetadataField;
  key: string;
}

/**
 * Human label for a field's ACTUAL iconik `field_type` — mirrors
 * `IconikMetadataService.humanMetadataFieldType`'s `drop_down`/`tag_cloud` cases
 * (`iconik-manager's packages/iconik/src/iconikMetadata.ts`; not imported here, this app keeps its own mirrored
 * copies of backend types/logic rather than depending on `@cinesys/iconik` directly — see this
 * app's `CLAUDE.md`). Glossary eligibility (`glossary.ts`'s `GLOSSARY_ELIGIBLE_FIELD_TYPES`) only
 * ever admits `drop_down`, so the "Add Field" hint this feeds only ever shows that label today —
 * the `tag_cloud` case and the generic fallback exist so this degrades to *something* readable if
 * ever called with a field type outside that set, rather than crashing.
 */
export const eligibleFieldTypeLabel = (fieldType: string): string => {
  if (fieldType === "drop_down") {
    return "Dropdown";
  }
  if (fieldType === "tag_cloud") {
    return "Tag Cloud";
  }
  return fieldType;
};

/**
 * "Add Field" destination options — every field across both configured views (asset + segment),
 * excluding whatever's already added under ANY mode (a field can only be one mode at a time, per
 * the unification design's mutual-exclusivity rule). There's no longer a mode-scoped field
 * universe to choose between here (Glossary's narrower "only eligible fields" rule is enforced on
 * the per-row Mode `Select` instead, once a field's already picked — see `FieldsScreen.tsx`'s
 * `typeOptionsFor`; Facial Recognition has no such restriction, any field is a valid destination)
 * — adding a field is just picking ANY field; what it does (Direct/Substitute/Format/Conditional/
 * Facial Recognition/Glossary) is chosen inside the row afterward.
 *
 * Two distinct iconik fields can share the same configured `label` within a group (e.g. a
 * curated "Descriptors" picklist field and its non-eligible "Descriptors_Auto-Tag" tag_cloud
 * companion, which also displays as "Descriptors") — picking either adds the correct underlying
 * field, but a user can't tell them apart before picking. Only the colliding options get their
 * raw field `name` appended as a disambiguator; the common (non-colliding) case is untouched.
 *
 * `glossaryEligibleFieldNames` flags which candidates could later be switched to Glossary mode
 * (see `FieldsScreen.tsx`'s `typeOptionsFor`) via each option's `extra` hint — surfaced before
 * the field is even added rather than only once its row is expanded.
 * `glossaryEligibleExtraFor` is called with the candidate's OWN `field_type` (`eligibleFieldTypeLabel`
 * above) so the hint reflects that field's real iconik representation instead of a single blanket
 * label; it's caller-rendered (icon + label, built in `FieldsScreen.tsx` where JSX is available)
 * rather than built here, since this file has no `.tsx` JSX support.
 *
 * `extraDestination` (optional) is the one destination that belongs to neither configured view —
 * today, only the always-available "Comments" destination (Seth Tanner, Monday 12926430722; see
 * `commentDestination.ts`). It's excluded the same "already added" way as any real field
 * (`addedFieldIds.has(extraDestination.fieldName)`) and folded into the same disambiguation/sort
 * pass below, just carrying its own caller-supplied `group` rather than one derived from a real
 * view's scope.
 */
export const buildAddFieldOptions = (
  flatFieldEntries: FlatFieldEntry[],
  addedFieldIds: ReadonlySet<string>,
  groupLabels: { asset: string; segment: string },
  glossaryEligibleFieldNames: ReadonlySet<string>,
  glossaryEligibleExtraFor: (fieldType: string) => ReactNode,
  extraDestination?: {
    key: string;
    fieldName: string;
    label: string;
    group: string;
  }
): SelectOption[] => {
  const candidates = flatFieldEntries
    .filter((entry) => !addedFieldIds.has(entry.field.name))
    .map((entry) => ({
      value: entry.key,
      label: entry.field.label || entry.field.name,
      group:
        entry.view.scope === "segment"
          ? groupLabels.segment
          : groupLabels.asset,
      extra: glossaryEligibleFieldNames.has(entry.field.name)
        ? glossaryEligibleExtraFor(entry.field.field_type)
        : undefined,
      fieldName: entry.field.name,
    }));

  if (extraDestination && !addedFieldIds.has(extraDestination.fieldName)) {
    candidates.push({
      value: extraDestination.key,
      label: extraDestination.label,
      group: extraDestination.group,
      extra: undefined,
      fieldName: extraDestination.fieldName,
    });
  }

  const countByGroupLabel = new Map<string, number>();
  for (const c of candidates) {
    const groupLabelKey = `${c.group}::${c.label}`;
    countByGroupLabel.set(
      groupLabelKey,
      (countByGroupLabel.get(groupLabelKey) ?? 0) + 1
    );
  }

  return candidates
    .map(({ fieldName, ...option }) => {
      const groupLabelKey = `${option.group}::${option.label}`;
      return (countByGroupLabel.get(groupLabelKey) ?? 0) > 1
        ? { ...option, label: `${option.label} — ${fieldName}` }
        : option;
    })
    .sort((a, b) =>
      a.group === b.group
        ? String(a.label).localeCompare(String(b.label))
        : String(a.group).localeCompare(String(b.group))
    );
};
